import React from "react";
import { Plus } from "lucide-react";
import { PencilIcon } from "../../../components/ui";
import {
  lockedProps,
  type GateProps,
} from "../../billing/hooks/useSubscriptionGate";
import { formatMealTime } from "../planMapping";
import type { MealSlot } from "../types/plans.types";

export interface MealSlotTabsProps {
  meals: MealSlot[];
  activeMealId: string;
  onSelect: (mealId: string) => void;
  /** The pencil on a pill — renaming a meal or moving its time. */
  onEdit: (meal: MealSlot) => void;
  onAdd: () => void;
  /** Set once the plan holds every meal name there is to add. */
  addDisabled?: boolean;
  /**
   * A plan that is only being read — an active one. The pills still switch
   * between the meals; the pencil and the add button are not drawn at all,
   * since the day they change is one the client already holds.
   */
  readOnly?: boolean;
  /**
   * Whether the workspace may write this plan at all — see
   * `useSubscriptionGate`. Different from `readOnly` above and drawn
   * differently: an active plan is finished, so the pencil and the add button
   * are simply not there, while a lapsed subscription is a thing that can be
   * put right, so they stay on the pills and say what to do about it.
   *
   * The pills themselves switch meals either way. Choosing which meal to look
   * at writes nothing.
   */
  gate?: GateProps;
}

/**
 * The day across the top of the builder: one pill per meal, in the order they
 * are eaten, and a square button to add another.
 *
 * Each pill is two buttons sharing one border rather than a button with
 * another inside it — a button may not contain interactive content, and the
 * pencil has to be reachable on its own. The wrapper is `presentation` so the
 * tabs are still owned by the tablist through it.
 */
const MealSlotTabs: React.FC<MealSlotTabsProps> = ({
  meals,
  activeMealId,
  onSelect,
  onEdit,
  onAdd,
  addDisabled = false,
  readOnly = false,
  gate = { disabled: false },
}) => (
  // `items-stretch`, so the add button takes its height from the pills rather
  // than carrying a fixed one of its own — a number that only matches them
  // until the type size or the padding moves.
  <div role="tablist" className="flex flex-wrap items-stretch gap-3">
    {meals.map((meal) => {
      const active = meal.id === activeMealId;

      return (
        <div
          key={meal.id}
          role="presentation"
          className={`flex items-stretch overflow-hidden rounded-[6px] transition-colors ${
            active ? "bg-[#00424B] text-white" : "bg-[#EFFBFD] text-[#00424B]"
          }`}
        >
          <button
            type="button"
            role="tab"
            aria-selected={active}
            onClick={() => onSelect(meal.id)}
            // The right padding closes up to match the left once the pencil
            // is gone: the space was there to sit the label off the rule.
            className={`flex items-center gap-2 py-2 pl-4 text-[12px] 3xl:text-[14px] focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand/50 ${
              readOnly ? "pr-4" : "pr-3"
            }`}
          >
            <span className="font-medium">{meal.name}</span>
            <span
              aria-hidden="true"
              // Bigger than the label it sits between, and `leading-none` so
              // the extra size does not grow the pill.
              className={`text-[20px] leading-none 3xl:text-[22px] ${
                active ? "text-[#3C656B]" : "text-[#D3DDDE]"
              }`}
            >
              •
            </span>
            <span className={active ? "text-white/90" : "text-ink-muted"}>
              {formatMealTime(meal.time)}
            </span>
          </button>

          {!readOnly && (
            <>
              {/* The rule between the meal time and the pencil. Its own element
                  rather than a border on the button, so it can be inset from
                  the pill's edges. */}
              <span
                aria-hidden="true"
                className={`my-1.5 w-px shrink-0 ${
                  active ? "bg-[#3C656B]" : "bg-[#D3DDDE]"
                }`}
              />

              <button
                type="button"
                aria-label={`Edit ${meal.name}`}
                // Turned back by hand rather than natively disabled, so the
                // press is answered rather than swallowed — see `lockedProps`.
                {...lockedProps(gate, () => onEdit(meal))}
                // The pencil's two states are a colour apart: pale on the
                // filled pill, dark teal on the pale one.
                className={`inline-flex w-8 items-center justify-center transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand/50 ${
                  active ? "text-[#F8FEFF]" : "text-[#00424B]"
                } ${gate.disabled ? "cursor-not-allowed opacity-50" : ""}`}
              >
                <PencilIcon className="h-4 w-4" />
              </button>
            </>
          )}
        </div>
      );
    })}

    {!readOnly && (
      <button
        type="button"
        aria-label="Add meal"
        // A plan that already holds every meal there is stays plainly disabled:
        // there is nothing to explain and nothing to do about it. A workspace
        // with no subscription is the other way round — that one has something
        // to do about it, so the press is turned back to the reason rather than
        // swallowed. See `lockedProps`.
        {...(addDisabled
          ? {
              disabled: true,
              title: "Every meal of the day is already on this plan",
            }
          : {
              ...lockedProps(gate, onAdd),
              title: gate.title ?? "Add meal",
            })}
        // The height comes from the row's `items-stretch`, so it always matches
        // the pills exactly. Only the width is set, to the same figure that
        // height works out to — `aspect-square` cannot do it, because a flex
        // item's automatic width is resolved from its content before the
        // stretch gives it a height to square off.
        className={`inline-flex w-9 3xl:w-10 items-center justify-center rounded-[6px] bg-[#EFFBFD] text-[#00424B] transition-colors ${
          addDisabled ? "cursor-not-allowed" : ""
        } ${gate.disabled ? "cursor-not-allowed opacity-50" : ""}`}
      >
        <Plus className="h-4 w-4" />
      </button>
    )}
  </div>
);

export default MealSlotTabs;
